import supabase from '../config/supabase';
import { SUPABASE_KEY, SUPABASE_URL } from '../config/database';
import { vendorCanReceiveOrder } from '../utils/vendorContacts';
import { resolvePoLinePackSize, formatPoOrderQuantity } from '../utils/poLinePack';
// The ONE order-step authority (max(MOQ, increment grid)); the same module the
// Savings Review math and the dispatch send revalidation use, so a quantity
// legal here is legal there.
import { validateOrderPackages } from '../../../supabase/functions/_shared/parAndMoq.mjs';

/**
 * Single source of truth for whether a draft PO can be sent.
 * Used by both PurchaseOrderCard (card-level button) and DraftOrderReview
 * (editor send button) so the blocker logic is always identical.
 *
 * @param {object} opts
 * @param {object} opts.vendorContactSnapshot  - po.vendor_contact_snapshot (or local state)
 * @param {string} opts.vendorNameSnapshot     - po.vendor_name_snapshot
 * @param {number|null} opts.vendorId          - po.vendor_id
 * @param {number} opts.lineCount              - po.line_count or visible item count
 * @param {Array}  opts.items                  - po.items array (fallback for lineCount)
 * @param {Object|Map} [opts.optionsById]      - linked purchase options by id; when
 *                                               provided, quantities on linked lines
 *                                               are validated against MOQ/increment
 * @param {function} [opts.quantityOf]         - item → quantity to judge (edited value)
 * @returns {{ canSend, hasVendor, hasEmail, hasItems, recipients, ruleViolations, blockerReason }}
 */
/**
 * One sentence explaining why a typed quantity is not one the vendor sells,
 * in Part-17 language ("Vendor minimum", "Ordered in"). `unit` is the linked
 * option's purchase_unit. Returns null for a legal quantity.
 */
export function describeOrderRuleViolation(check, unit = 'package') {
  if (!check || check.legal) return null;
  const noun = String(unit || 'package').trim() || 'package';
  const plural = (n) => (Number(n) === 1 || noun.endsWith('s') ? noun : `${noun}s`);
  if (check.reason === 'no_quantity') {
    return `has no quantity — set one or remove the line`;
  }
  if (check.reason === 'below_moq') {
    return `requires a minimum of ${check.moq} ${plural(check.moq)}`;
  }
  if (check.reason === 'off_increment') {
    return check.increment === 1
      ? `is ordered in whole ${plural(2)}`
      : `is ordered in steps of ${check.increment} ${plural(check.increment)}`;
  }
  return `does not match this vendor product's ordering rules`;
}

/**
 * Per-line ordering-rule validation for a draft's items. Only lines LINKED to
 * a purchase option are judged — a manual line has no vendor rules on record
 * and none are invented for it. Quantities are validated by the ONE
 * authoritative order-step implementation (_shared/parAndMoq.mjs), the same
 * math Savings Review and the server's send revalidation use.
 *
 * @param {Array} items    purchase_order_items rows (need purchase_option_id)
 * @param {Object|Map} optionsById  purchase option rows keyed by id
 * @param {function} [quantityOf]   item → the quantity to judge (defaults to
 *                                  the stored order_quantity)
 * @returns {Array<{ id, description, quantity, message, check, option }>}
 */
export function validateDraftItemsAgainstOptions(items = [], optionsById = {}, quantityOf) {
  const lookup = (id) => {
    if (id === null || id === undefined) return null;
    if (optionsById instanceof Map) return optionsById.get(String(id)) || optionsById.get(id) || null;
    return optionsById[String(id)] ?? optionsById[id] ?? null;
  };
  const violations = [];
  (items || []).forEach((item) => {
    const option = lookup(item?.purchase_option_id);
    if (!option) return;
    const quantity = typeof quantityOf === 'function'
      ? quantityOf(item)
      : Number.parseFloat(item?.order_quantity);
    const check = validateOrderPackages({
      packages: quantity,
      moqPackages: option.moq_packages,
      orderIncrement: option.order_increment,
      allowsFractional: option.allows_fractional_packages === true,
    });
    if (check.legal) return;
    const message = describeOrderRuleViolation(check, option.purchase_unit);
    violations.push({
      id: item.id,
      description: item.description || option.vendor_description || 'Line',
      quantity,
      message,
      check,
      option,
    });
  });
  return violations;
}

export function computeDraftSendEligibility({
  vendorContactSnapshot,
  vendorNameSnapshot,
  vendorId,
  lineCount,
  items,
  optionsById,
  quantityOf,
  priceOf,
} = {}) {
  const contact = vendorContactSnapshot || {};
  const recipients =
    Array.isArray(contact.orderRecipientEmails) && contact.orderRecipientEmails.length > 0
      ? contact.orderRecipientEmails
      : contact.contactEmail ? [contact.contactEmail]
      : contact.email ? [contact.email]
      : [];
  const hasEmail = recipients.length > 0;
  const hasItems = (lineCount || 0) > 0 || (items || []).length > 0;
  const hasVendor = !!(vendorNameSnapshot || vendorId);

  // A quantity edit may not produce an order the vendor will refuse. Judged
  // only when the caller supplies the linked options; callers without them
  // keep the old vendor/email/items gate (the server still revalidates).
  const ruleViolations = optionsById
    ? validateDraftItemsAgainstOptions(items, optionsById, quantityOf)
    : [];

  // A line with NO price on record (unit_price NULL, migration
  // 20260821120000) may not be emailed to a vendor. $0.00 reads as "free" and
  // is exactly the outcome this whole path exists to stop; an unpriced line is
  // work for the buyer, one field wide, and the send waits for it.
  // Only an EXPLICIT null (or blank) is "no price on record". A line object
  // that simply carries no price KEY has told us nothing, and a gate that
  // read silence as a blocker would refuse every caller that passes a
  // partial line — the same over-reading that made 0 mean "priced".
  const statedPrice = (item) => {
    if (priceOf) return priceOf(item);
    if (item && Object.prototype.hasOwnProperty.call(item, 'unit_price')) return item.unit_price;
    if (item && Object.prototype.hasOwnProperty.call(item, 'unitPrice')) return item.unitPrice;
    return undefined;
  };
  const unpricedLines = (items || []).filter((item) => {
    const price = statedPrice(item);
    return price === null || price === '';
  });

  const blockerReason = !hasVendor
    ? 'No vendor assigned to this draft'
    : !hasEmail
    ? 'Add a vendor email to enable sending'
    : !hasItems
    ? 'Add at least one item to enable sending'
    : unpricedLines.length > 0
    ? `${unpricedLines.length === 1 ? `${unpricedLines[0].description || 'One line'} has` : `${unpricedLines.length} lines have`} no price. Enter a price per ${unpricedLines[0]?.unit || 'order unit'} before sending.`
    : ruleViolations.length > 0
    ? `${ruleViolations[0].description} ${ruleViolations[0].message}. Change the quantity or edit this vendor product's ordering rules.`
    : '';

  return {
    canSend: hasVendor && hasEmail && hasItems && ruleViolations.length === 0 && unpricedLines.length === 0,
    hasVendor,
    hasEmail,
    hasItems,
    recipients,
    ruleViolations,
    unpricedLines,
    blockerReason,
  };
}

/**
 * Module-level cache to avoid re-fetching the same user profile across
 * multiple PO cards rendered in the same session.
 */
const _profileNameCache = new Map();

/**
 * Return a compact "First L." name for a user from user_profiles.
 * Prefers the editable team-facing display_name; falls back to the legacy
 * first/last columns. Returns null when no profile exists or names are blank.
 */
export async function resolveUserProfileCompactName(userId) {
  if (!userId) return null;
  if (_profileNameCache.has(userId)) return _profileNameCache.get(userId);
  try {
    const { data } = await supabase
      .from('user_profiles')
      .select('display_name, first_name, last_name')
      .eq('id', userId)
      .maybeSingle();
    let name = null;
    const display = (data?.display_name || '').trim();
    if (display) {
      const parts = display.split(/\s+/).filter(Boolean);
      name = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
    } else {
      const first = (data?.first_name || '').trim();
      const last = (data?.last_name || '').trim();
      name = first && last ? `${first} ${last[0]}.` : first || null;
    }
    _profileNameCache.set(userId, name);
    return name;
  } catch {
    return null;
  }
}

/**
 * Return the best human-readable label for who created/sent a PO.
 * Prefers ordered_by_name when it looks like a real name (no "@").
 * Falls back to ordered_by_email when ordered_by_name is an email or empty.
 */
export function formatOrderedByLabel(orderedByName, orderedByEmail) {
  const name = (orderedByName || '').trim();
  if (name && !name.includes('@')) return name;
  return (orderedByEmail || name || 'Unknown').trim();
}

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

const formatCurrency = (value) => currencyFormatter.format(Number.isFinite(value) ? value : 0);
const formatNumber = (value) => numberFormatter.format(Number.isFinite(value) ? value : 0);

const toText = (value, fallback = '') => {
  if (value == null) return fallback;
  const next = String(value).trim();
  return next || fallback;
};

// Funnel: "first order created" — fired once per store per browser when a
// purchase order is successfully created/sent. No PII in the event payload.
function trackFirstOrderCreated(storeId) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const key = 'ct_first_order_' + (storeId || 'unknown');
    if (localStorage.getItem(key) === '1') return;
    localStorage.setItem(key, '1');
    window.gtag('event', 'first_order_created', {});
  } catch { /* analytics must never block ordering */ }
}

const STATUS_ALIASES = {
  sent: 'sent_by_email',
  delivered: 'sent_by_email',
  opened: 'sent_by_email',
  clicked: 'sent_by_email',
  pending_approval: 'pending_approval',
  blocked_no_email: 'sent_outside_countertop',
  sent_outside_countertop: 'sent_outside_countertop',
  blocked: 'not_sent_vendor_missing_email',
  no_vendor_assigned: 'not_sent_no_vendor_assigned',
  error: 'error',
  sending: 'sending',
};

const normalizeSendStatus = (status) => STATUS_ALIASES[status] || status || 'error';

const normalizeLineItem = (item = {}) => {
  const orderQuantity = parseFloat(item.orderQuantity ?? item.quantity_to_order ?? 0) || 0;
  const unitPrice = parseFloat(item.unitPrice ?? item.unit_price ?? 0) || 0;
  const lineTotal = Number.isFinite(item.lineTotal) ? item.lineTotal : orderQuantity * unitPrice;
  return {
    inventoryItemId: item.inventoryItemId || item.inventory_item_id || null,
    description: toText(item.description, 'Unnamed Item'),
    brand: toText(item.brand),
    location: toText(item.location, '-'),
    unit: toText(item.unit, ''),
    // What one of those order units contains. Carried on every line the same
    // way the unit is, so the vendor email and the PDF can say "1 case
    // (8 bags × 12 buns)" rather than an unverifiable "1".
    packSize: resolvePoLinePackSize(item),
    quantity: parseFloat(item.quantity) || 0,
    parLevel: parseFloat(item.parLevel) || 0,
    shortage: parseFloat(item.shortage) || 0,
    orderQuantity,
    unitPrice,
    lineTotal,
    notes: toText(item.notes),
  };
};

const summarizeSource = (sourceMetadata = {}) => {
  if (sourceMetadata?.sourceType === 'count_complete') {
    return toText(sourceMetadata?.countSessionName, 'Count Session');
  }
  return 'Order Report';
};

const buildPurchaseOrderFooter = () => (
  'Sent with CounterTop-APP at www.countertop-app.com\n'
  + 'Inventory for Restaurants and Bars that does not take 365 days to master'
);

const buildPoNumberPreview = (vendorName) => {
  const normalizedVendor = toText(vendorName, 'vendor').replace(/[^a-z0-9]/gi, '').slice(0, 6).toUpperCase() || 'VENDOR';
  const timestamp = new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 12);
  return `PO-${normalizedVendor}-${timestamp}`;
};

export function buildOrderReportByVendor(orderLines = []) {
  const vendorBuckets = new Map();
  (orderLines || []).forEach((line) => {
    const vendorNameRaw = toText(line.vendorName || line.vendor);
    const hasAssignedVendor = Boolean(vendorNameRaw);
    const vendorName = hasAssignedVendor ? vendorNameRaw : 'No Vendor Assigned';
    const key = line.vendorId || vendorName.toLowerCase();
    const contact = line.contact || {};
    if (!vendorBuckets.has(key)) {
      vendorBuckets.set(key, {
        vendorId: line.vendorId || null,
        vendorName,
        contact,
        hasAssignedVendor,
        canSend: hasAssignedVendor && vendorCanReceiveOrder(contact),
        sendReadinessReason: hasAssignedVendor
          ? (
            vendorCanReceiveOrder(contact)
              ? ''
              : 'Not sent — vendor has no contact email. Add an email, or mark as sent outside Countertop.'
          )
          : 'Not sent — no vendor assigned. Use Items + AI to assign vendors.',
        items: [],
      });
    }
    vendorBuckets.get(key).items.push(normalizeLineItem(line));
  });
  return [...vendorBuckets.values()].map((vendor) => {
    const total = vendor.items.reduce((sum, item) => sum + item.lineTotal, 0);
    return {
      ...vendor,
      totalOrderValue: total,
      totalOrderQty: vendor.items.reduce((sum, item) => sum + item.orderQuantity, 0),
    };
  });
}

export function buildPurchaseOrder(vendorOrder, sourceMetadata = {}, options = {}) {
  const items = Array.isArray(vendorOrder?.items) ? vendorOrder.items.map(normalizeLineItem) : [];
  const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);
  const orderedByName = toText(options?.orderedByName || sourceMetadata?.orderedByName || sourceMetadata?.orderedByEmail, 'Unknown User');
  const orderedByEmail = toText(options?.orderedByEmail || sourceMetadata?.orderedByEmail);
  return {
    purchaseOrderId: vendorOrder?.purchaseOrderId || null,
    poNumber: toText(vendorOrder?.poNumber, buildPoNumberPreview(vendorOrder?.vendorName)),
    store: {
      storeId: sourceMetadata?.storeId || null,
      storeName: toText(sourceMetadata?.storeName),
      storeAddress: toText(sourceMetadata?.storeAddress),
      locationName: toText(sourceMetadata?.locationName),
    },
    vendorId: vendorOrder?.vendorId || null,
    vendorName: toText(vendorOrder?.vendorName, 'Unknown Vendor'),
    contact: {
      contactName: toText(vendorOrder?.contact?.contactName || vendorOrder?.contactName),
      contactEmail: toText(vendorOrder?.contact?.contactEmail || vendorOrder?.contactEmail),
      contactPhone: toText(vendorOrder?.contact?.contactPhone || vendorOrder?.contactPhone),
      secondaryContactName: toText(vendorOrder?.contact?.secondaryContactName),
      secondaryContactEmail: toText(vendorOrder?.contact?.secondaryContactEmail),
      secondaryContactPhone: toText(vendorOrder?.contact?.secondaryContactPhone),
      generalEmail: toText(vendorOrder?.contact?.generalEmail),
      generalPhone: toText(vendorOrder?.contact?.generalPhone),
      orderingMethod: toText(vendorOrder?.contact?.orderingMethod),
      website: toText(vendorOrder?.contact?.website),
      address: toText(vendorOrder?.contact?.address),
      sendOrdersToContactEmail: vendorOrder?.contact?.sendOrdersToContactEmail !== false,
      sendOrdersToGeneralEmail: vendorOrder?.contact?.sendOrdersToGeneralEmail === true,
      orderRecipientEmails: Array.isArray(vendorOrder?.contact?.orderRecipientEmails)
        ? vendorOrder.contact.orderRecipientEmails.filter(Boolean)
        : [],
    },
    source: {
      sourceType: toText(sourceMetadata?.sourceType, 'report_preview'),
      reportGeneratedAt: sourceMetadata?.reportGeneratedAt || new Date().toISOString(),
      reportSessionIds: sourceMetadata?.reportSessionIds || [],
      countSessionId: sourceMetadata?.countSessionId || null,
      countSessionName: toText(sourceMetadata?.countSessionName),
    },
    orderedBy: {
      userId: sourceMetadata?.orderedByUserId || null,
      name: orderedByName,
      email: orderedByEmail,
    },
    notes: toText(vendorOrder?.notes || sourceMetadata?.notes),
    requestedDeliveryDate: options?.requestedDeliveryDate || vendorOrder?.requestedDeliveryDate || null,
    deliveryNotes: options?.deliveryNotes || vendorOrder?.deliveryNotes || null,
    currency: 'USD',
    items,
    totals: {
      subtotal,
      total: subtotal,
      lineCount: items.length,
      totalOrderQuantity: items.reduce((sum, item) => sum + item.orderQuantity, 0),
    },
    sender: {
      fromEmail: 'no-reply@countertop-app.com',
      footer: buildPurchaseOrderFooter(),
    },
    metadata: {
      sourceSummary: summarizeSource(sourceMetadata),
      createdAt: new Date().toISOString(),
    },
  };
}

export function buildPurchaseOrdersFromReport(vendorOrders = [], sourceMetadata = {}, options = {}) {
  return (vendorOrders || []).map((vendorOrder) => buildPurchaseOrder(vendorOrder, sourceMetadata, options));
}

const PO_LOGO_URL = 'https://www.countertop-app.com/icons/Countertop-app_logo_text_only_counter.png';

const escapeHtmlPo = (value) => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatDeliveryDatePo = (value) => {
  if (!value) return 'Not specified';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return String(value); }
};

export function previewPurchaseOrder(purchaseOrder) {
  const vendorName = toText(purchaseOrder?.vendorName, 'Vendor');
  const poNumber = toText(purchaseOrder?.poNumber, 'PO-DRAFT');
  const storeName = toText(purchaseOrder?.store?.storeName, '');
  const storeAddress = toText(purchaseOrder?.store?.storeAddress, '');
  const orderedByName = toText(purchaseOrder?.orderedBy?.name, 'Unknown User');
  const senderLabel = orderedByName && orderedByName !== 'Unknown User' ? orderedByName : 'CounterTop';
  const subject = `[Countertop] Purchase Order ${poNumber} for ${vendorName}`;

  const items = purchaseOrder?.items || [];
  const total = purchaseOrder?.totals?.total || 0;
  const deliveryDate = purchaseOrder?.requestedDeliveryDate || null;
  const deliveryNotes = purchaseOrder?.deliveryNotes || null;
  const contact = purchaseOrder?.contact || {};

  const textLines = [
    `Purchase Order: ${poNumber}`,
    storeName ? `Store: ${storeName}` : '',
    storeAddress ? `Address: ${storeAddress}` : '',
    `Vendor: ${vendorName}`,
    `Ordered by: ${orderedByName}`,
    `Order date: ${new Date().toLocaleString()}`,
    `Requested delivery: ${deliveryDate ? formatDeliveryDatePo(deliveryDate) : 'Not specified'}`,
    `Delivery notes: ${deliveryNotes || 'Not specified'}`,
    '',
    `Recipient: ${toText(contact.contactName, 'Not set')}`,
    `Email: ${toText(contact.contactEmail, 'Not set')}`,
    `Phone: ${toText(contact.contactPhone, 'Not set')}`,
    '',
    'Line items:',
    ...items.map((item, index) => {
      const packSize = resolvePoLinePackSize(item);
      return [
        `${index + 1}. ${toText(item.description, 'Unnamed Item')} (${toText(item.location, '-')})`,
        ...(packSize ? [`   Pack / Size: ${packSize}`] : []),
        `   Qty: ${formatPoOrderQuantity(formatNumber(item.orderQuantity), item.unit)} | Unit Price: ${formatCurrency(item.unitPrice)} | Line Total: ${formatCurrency(item.lineTotal)}`,
      ];
    }).flat(),
    '',
    `Total: ${formatCurrency(total)}`,
    '',
    `Sent by ${senderLabel}`,
    'Inventory for Restaurants and Bars that does not take 365 days to master.',
    'www.countertop-app.com',
  ].filter((l) => l !== null).join('\n');

  const rowsHtml = items.map((item) => `
    <tr>
      <td style="padding:7px 10px;border:1px solid #d8cdc3;">${escapeHtmlPo(toText(item.description, 'Unnamed Item'))}</td>
      <td style="padding:7px 10px;border:1px solid #d8cdc3;">${escapeHtmlPo(toText(item.location, '-'))}</td>
      <td style="padding:7px 10px;border:1px solid #d8cdc3;">${escapeHtmlPo(resolvePoLinePackSize(item) || '—')}</td>
      <td style="padding:7px 10px;border:1px solid #d8cdc3;text-align:right;font-variant-numeric:tabular-nums;">${escapeHtmlPo(formatPoOrderQuantity(formatNumber(item.orderQuantity), item.unit))}</td>
      <td style="padding:7px 10px;border:1px solid #d8cdc3;text-align:right;font-variant-numeric:tabular-nums;">${escapeHtmlPo(formatCurrency(item.unitPrice))}</td>
      <td style="padding:7px 10px;border:1px solid #d8cdc3;text-align:right;font-variant-numeric:tabular-nums;font-weight:600;">${escapeHtmlPo(formatCurrency(item.lineTotal))}</td>
    </tr>`).join('');

  const storeHeaderRight = [
    storeName ? `<strong style="color:#3d2518;">${escapeHtmlPo(storeName)}</strong>` : '',
    storeAddress ? `<span>${escapeHtmlPo(storeAddress)}</span>` : '',
  ].filter(Boolean).join('<br>');

  const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f5f0e8;font-family:Arial,Helvetica,sans-serif;color:#3d2518;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f0e8;padding:24px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#fff;border-radius:10px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.08);">

        <tr>
          <td style="padding:20px 28px 16px;border-bottom:2px solid #5A4A42;">
            <table width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td valign="top">
                  <div style="font-size:20px;font-weight:800;color:#3d2518;letter-spacing:0.02em;">Purchase Order</div>
                  <div style="font-size:18px;font-weight:800;color:#5A4A42;letter-spacing:0.04em;">${escapeHtmlPo(poNumber)}</div>
                </td>
                <td valign="top" align="right" style="font-size:12px;color:#6f6159;line-height:1.6;">
                  ${storeHeaderRight || '&nbsp;'}
                </td>
              </tr>
            </table>
          </td>
        </tr>

        <tr>
          <td style="padding:16px 28px;border-bottom:1px solid #f0e6dd;">
            <table width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td width="50%" valign="top" style="font-size:13px;padding-right:12px;">
                  <div style="font-size:10px;font-weight:700;color:#5A4A42;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Vendor</div>
                  <div style="margin-bottom:10px;">${escapeHtmlPo(vendorName)}</div>
                  <div style="font-size:10px;font-weight:700;color:#5A4A42;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Contact</div>
                  <div style="margin-bottom:2px;">${escapeHtmlPo(toText(contact.contactName, '—'))}</div>
                  <div style="margin-bottom:2px;">${escapeHtmlPo(toText(contact.contactEmail, '—'))}</div>
                  <div>${escapeHtmlPo(toText(contact.contactPhone, '—'))}</div>
                </td>
                <td width="50%" valign="top" style="font-size:13px;padding-left:12px;">
                  <div style="font-size:10px;font-weight:700;color:#5A4A42;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Ordered By</div>
                  <div style="margin-bottom:10px;">${escapeHtmlPo(orderedByName)}</div>
                  <div style="font-size:10px;font-weight:700;color:#5A4A42;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Requested Delivery</div>
                  <div style="margin-bottom:10px;">${escapeHtmlPo(formatDeliveryDatePo(deliveryDate))}</div>
                  ${deliveryNotes ? `<div style="font-size:10px;font-weight:700;color:#5A4A42;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:3px;">Delivery Notes</div><div>${escapeHtmlPo(deliveryNotes)}</div>` : ''}
                </td>
              </tr>
            </table>
          </td>
        </tr>

        <tr>
          <td style="padding:0 28px 20px;">
            <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-size:13px;margin-top:16px;">
              <thead>
                <tr style="background:#f7f1ea;">
                  <th style="padding:8px 10px;border:1px solid #d8cdc3;text-align:left;font-weight:700;color:#5A4A42;">Item</th>
                  <th style="padding:8px 10px;border:1px solid #d8cdc3;text-align:left;font-weight:700;color:#5A4A42;">Location</th>
                  <th style="padding:8px 10px;border:1px solid #d8cdc3;text-align:left;font-weight:700;color:#5A4A42;">Pack / Size</th>
                  <th style="padding:8px 10px;border:1px solid #d8cdc3;text-align:right;font-weight:700;color:#5A4A42;">Qty</th>
                  <th style="padding:8px 10px;border:1px solid #d8cdc3;text-align:right;font-weight:700;color:#5A4A42;">Unit Price</th>
                  <th style="padding:8px 10px;border:1px solid #d8cdc3;text-align:right;font-weight:700;color:#5A4A42;">Line Total</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
              <tfoot>
                <tr style="background:#fbf6f0;">
                  <th colspan="5" style="padding:8px 10px;border:1px solid #d8cdc3;text-align:right;font-weight:700;">Total</th>
                  <th style="padding:8px 10px;border:1px solid #d8cdc3;text-align:right;font-weight:700;">${escapeHtmlPo(formatCurrency(total))}</th>
                </tr>
              </tfoot>
            </table>
          </td>
        </tr>

        <tr>
          <td style="padding:16px 28px;background:#faf6f0;border-top:1px solid #e9dfd6;">
            <table cellpadding="0" cellspacing="0">
              <tr>
                <td valign="middle" style="padding-right:14px;">
                  <img src="${PO_LOGO_URL}" alt="CounterTop" width="80" style="display:block;height:auto;" />
                </td>
                <td valign="middle" style="font-size:11px;color:#6f6159;line-height:1.7;">
                  <div style="font-weight:700;color:#3d2518;">Sent by ${escapeHtmlPo(senderLabel)}</div>
                  <div>Inventory for Restaurants and Bars that does not take 365 days to master.</div>
                  <div><a href="https://www.countertop-app.com" style="color:#526B7C;text-decoration:none;">www.countertop-app.com</a></div>
                </td>
              </tr>
            </table>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body></html>`;

  return { subject, text: textLines, html };
}

async function getSessionAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

async function invokePurchaseOrderFunction(action, payload) {
  const token = await getSessionAccessToken();
  if (!token) {
    throw new Error('Authentication session not found.');
  }
  const response = await fetch(`${SUPABASE_URL}/functions/v1/purchase-order-dispatch`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: SUPABASE_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, ...payload }),
  });
  // Read as text first so we can still surface Supabase runtime errors
  // (plain-text Deno stack traces) when the function returns a non-JSON body.
  const rawBody = await response.text();
  let json = {};
  try { json = rawBody ? JSON.parse(rawBody) : {}; } catch { /* non-JSON body */ }
  if (!response.ok) {
    console.error('[purchase-order-dispatch]', response.status, 'body:', rawBody);
    const detail = json?.error
      || (rawBody && rawBody.length < 500 ? rawBody : null)
      || `Purchase order function failed (${response.status}).`;
    throw new Error(detail);
  }
  return json;
}

export async function sendPurchaseOrder(purchaseOrder, options = {}) {
  const recipientList = Array.isArray(purchaseOrder?.contact?.orderRecipientEmails)
    ? purchaseOrder.contact.orderRecipientEmails.filter(Boolean)
    : [];
  const destinationEmail = recipientList[0] || toText(purchaseOrder?.contact?.contactEmail);
  const markOutsideCountertop = options?.markOutsideCountertop === true;
  if (!destinationEmail) {
    if (markOutsideCountertop) {
      try {
        const result = await invokePurchaseOrderFunction('dispatch', {
          purchaseOrder,
          emailPreview: options?.emailPreview || previewPurchaseOrder(purchaseOrder),
          sendMode: 'outside_countertop',
          reason: 'vendor_missing_contact_email',
        });
        return {
          status: normalizeSendStatus(result?.deliveryStatus || result?.status || 'sent_outside_countertop'),
          purchaseOrderId: result?.purchaseOrderId || null,
          poNumber: result?.poNumber || null,
          vendorId: purchaseOrder?.vendorId || null,
          vendorName: purchaseOrder?.vendorName || 'Unknown Vendor',
          destinationEmail: '',
          approvalStatus: result?.approvalStatus || 'not_required',
          message: result?.message || 'Marked as sent outside Countertop.',
        };
      } catch (error) {
        return {
          status: 'error',
          purchaseOrderId: purchaseOrder?.purchaseOrderId || null,
          poNumber: purchaseOrder?.poNumber || null,
          vendorId: purchaseOrder?.vendorId || null,
          vendorName: purchaseOrder?.vendorName || 'Unknown Vendor',
          destinationEmail: '',
          message: error?.message || 'Unable to mark as sent outside Countertop.',
        };
      }
    }
    return {
      status: 'not_sent_vendor_missing_email',
      vendorId: purchaseOrder?.vendorId || null,
      vendorName: purchaseOrder?.vendorName || 'Unknown Vendor',
      message: 'Not sent — vendor has no contact email.',
      purchaseOrderId: purchaseOrder?.purchaseOrderId || null,
      poNumber: purchaseOrder?.poNumber || null,
    };
  }
  try {
    const result = await invokePurchaseOrderFunction('dispatch', {
      purchaseOrder,
      emailPreview: options?.emailPreview || previewPurchaseOrder(purchaseOrder),
    });
    if (result?.purchaseOrderId) trackFirstOrderCreated(purchaseOrder?.storeId);
    return {
      status: normalizeSendStatus(result?.deliveryStatus || result?.status || 'sent_by_email'),
      purchaseOrderId: result?.purchaseOrderId || null,
      poNumber: result?.poNumber || null,
      vendorId: purchaseOrder?.vendorId || null,
      vendorName: purchaseOrder?.vendorName || 'Unknown Vendor',
      destinationEmail,
      approvalStatus: result?.approvalStatus || 'not_required',
      message: result?.message || '',
    };
  } catch (error) {
    return {
      status: 'error',
      purchaseOrderId: purchaseOrder?.purchaseOrderId || null,
      poNumber: purchaseOrder?.poNumber || null,
      vendorId: purchaseOrder?.vendorId || null,
      vendorName: purchaseOrder?.vendorName || 'Unknown Vendor',
      destinationEmail,
      message: error?.message || 'Send failed.',
    };
  }
}

export async function sendPurchaseOrdersBatch(vendorOrders = [], options = {}) {
  const sourceMetadata = options?.sourceMetadata || {};
  const normalizedVendorOrders = Array.isArray(vendorOrders) ? vendorOrders : [];
  const noVendorAssignedGroups = normalizedVendorOrders.filter((vendorOrder) => vendorOrder?.hasAssignedVendor === false);
  const sendableVendorOrders = normalizedVendorOrders.filter((vendorOrder) => vendorOrder?.hasAssignedVendor !== false);
  const purchaseOrders = buildPurchaseOrdersFromReport(sendableVendorOrders, sourceMetadata, options);
  const markOutsideForMissingEmail = options?.markOutsideForMissingEmail === true;
  const results = [];

  noVendorAssignedGroups.forEach((vendorOrder) => {
    results.push({
      status: 'not_sent_no_vendor_assigned',
      vendorId: null,
      vendorName: vendorOrder?.vendorName || 'No Vendor Assigned',
      message: 'Not sent — no vendor assigned.',
      purchaseOrder: null,
      itemCount: Array.isArray(vendorOrder?.items) ? vendorOrder.items.length : 0,
      skipped: true,
    });
  });

  for (const purchaseOrder of purchaseOrders) {
    if (typeof options?.onStatus === 'function') {
      options.onStatus({
        stage: 'sending',
        vendorName: purchaseOrder.vendorName,
        vendorId: purchaseOrder.vendorId,
      });
    }
    // eslint-disable-next-line no-await-in-loop
    const result = await sendPurchaseOrder(purchaseOrder, {
      emailPreview: previewPurchaseOrder(purchaseOrder),
      markOutsideCountertop: markOutsideForMissingEmail,
    });
    results.push({
      ...result,
      status: normalizeSendStatus(result?.status),
      purchaseOrder,
      itemCount: Array.isArray(purchaseOrder?.items) ? purchaseOrder.items.length : 0,
    });
    if (typeof options?.onStatus === 'function') {
      options.onStatus({
        stage: 'complete',
        vendorName: purchaseOrder.vendorName,
        vendorId: purchaseOrder.vendorId,
        status: normalizeSendStatus(result.status),
      });
    }
  }
  return {
    results,
    sentByEmail: results.filter((result) => result.status === 'sent_by_email').length,
    pendingApproval: results.filter((result) => result.status === 'pending_approval').length,
    sentOutsideCountertop: results.filter((result) => result.status === 'sent_outside_countertop').length,
    notSentNoVendor: results
      .filter((result) => result.status === 'not_sent_no_vendor_assigned')
      .reduce((sum, result) => sum + Number(result.itemCount || 0), 0),
    notSentMissingEmail: results
      .filter((result) => result.status === 'not_sent_vendor_missing_email')
      .reduce((sum, result) => sum + Number(result.itemCount || 0), 0),
    errored: results.filter((result) => result.status === 'error').length,
  };
}

export async function requestPurchaseOrderApproval(purchaseOrder) {
  return invokePurchaseOrderFunction('request_approval', { purchaseOrder });
}

export async function approvePurchaseOrder(token) {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/purchase-order-approve?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: {
      apikey: SUPABASE_KEY,
    },
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`Approval failed (${response.status}).`);
  }
  return text;
}

export async function resendPurchaseOrder(purchaseOrderId) {
  return invokePurchaseOrderFunction('resend', { purchaseOrderId });
}

export async function handlePurchaseOrderWebhook() {
  throw new Error('handlePurchaseOrderWebhook is server-only and handled by the purchase-order-webhook edge function.');
}

// ---------------------------------------------------------------------------
// Orders page helpers — listing, item search, revisions, custom recipients
// ---------------------------------------------------------------------------

const FORMATTERS = { currencyFormatter, numberFormatter };

export function formatPoCurrency(value) {
  return FORMATTERS.currencyFormatter.format(Number.isFinite(Number(value)) ? Number(value) : 0);
}

/**
 * A line's price for display: "Price needed" when there is none on record.
 *
 * `formatPoCurrency` coerces NULL to 0, which is right for a TOTAL (a total of
 * nothing is nothing) and wrong for a line price, where $0.00 tells the reader
 * the item is free. `purchase_order_items.unit_price` is nullable exactly so
 * those two states are distinguishable (migration 20260821120000) — this is
 * where the distinction reaches a screen.
 */
export const PO_PRICE_NEEDED_LABEL = 'Price needed';

export function formatPoLinePrice(value) {
  if (value === null || value === undefined || value === '') return PO_PRICE_NEEDED_LABEL;
  return formatPoCurrency(value);
}

export function formatPoQuantity(value) {
  return FORMATTERS.numberFormatter.format(Number.isFinite(Number(value)) ? Number(value) : 0);
}

const lineItemKey = (item = {}) => {
  const id = item.inventoryItemId ?? item.inventory_item_id;
  if (id != null && id !== '') return `id:${id}`;
  const desc = toText(item.description).toLowerCase();
  const loc = toText(item.location).toLowerCase();
  return `desc:${desc}|loc:${loc}`;
};

export function buildRevisionDiff(originalItems = [], revisedItems = []) {
  const originals = new Map();
  (originalItems || []).forEach((item) => originals.set(lineItemKey(item), item));
  const added = [];
  const changed = [];
  const unchanged = [];
  (revisedItems || []).forEach((item) => {
    const key = lineItemKey(item);
    const prior = originals.get(key);
    if (!prior) {
      added.push(item);
      return;
    }
    originals.delete(key);
    const oldQty = Number(prior.orderQuantity ?? prior.order_quantity ?? 0);
    const newQty = Number(item.orderQuantity ?? item.order_quantity ?? 0);
    const oldPrice = Number(prior.unitPrice ?? prior.unit_price ?? 0);
    const newPrice = Number(item.unitPrice ?? item.unit_price ?? 0);
    if (oldQty !== newQty || oldPrice !== newPrice) {
      changed.push({ before: prior, after: item });
    } else {
      unchanged.push(item);
    }
  });
  const removed = Array.from(originals.values());
  const lineTotal = (item) => Number(item.lineTotal ?? item.line_total ?? 0);
  return {
    added,
    removed,
    changed,
    unchanged,
    addedTotal: added.reduce((s, it) => s + lineTotal(it), 0),
    removedTotal: removed.reduce((s, it) => s + lineTotal(it), 0),
    changedDeltaTotal: changed.reduce((s, pair) => s + (lineTotal(pair.after) - lineTotal(pair.before)), 0),
  };
}

export function previewPurchaseOrderRevisionEmail({ original, revised, diff }) {
  const poNumber = toText(revised?.poNumber, 'PO-DRAFT');
  const parentPoNumber = toText(original?.poNumber, 'PO-PARENT');
  const vendorName = toText(revised?.vendorName || original?.vendorName, 'Vendor');
  const subject = `[AMENDED] Purchase Order ${poNumber} for ${vendorName}`;
  const subtotal = Number(revised?.totals?.subtotal || 0);
  const oldSubtotal = Number(original?.totals?.subtotal || original?.subtotal || 0);
  const headerLines = [
    `AMENDED PURCHASE ORDER — ${poNumber}`,
    `Replaces prior order: ${parentPoNumber}`,
    `Vendor: ${vendorName}`,
    `Items added: ${diff.added.length} | removed: ${diff.removed.length} | changed: ${diff.changed.length}`,
    `Previous total: ${formatPoCurrency(oldSubtotal)} -> New total: ${formatPoCurrency(subtotal)} (delta ${formatPoCurrency(subtotal - oldSubtotal)})`,
    '',
  ];
  const bodyLines = [];
  diff.unchanged.forEach((it) => bodyLines.push(`  - ${toText(it.description, 'Item')} (${toText(it.location, '-')}): qty ${formatPoQuantity(it.orderQuantity)} @ ${formatPoCurrency(it.unitPrice)}`));
  diff.changed.forEach(({ before, after }) => bodyLines.push(`  ~ ${toText(after.description, 'Item')} (${toText(after.location, '-')}): was qty ${formatPoQuantity(before.orderQuantity)} @ ${formatPoCurrency(before.unitPrice)} -> now qty ${formatPoQuantity(after.orderQuantity)} @ ${formatPoCurrency(after.unitPrice)} [CHANGED]`));
  diff.added.forEach((it) => bodyLines.push(`  + ${toText(it.description, 'Item')} (${toText(it.location, '-')}): qty ${formatPoQuantity(it.orderQuantity)} @ ${formatPoCurrency(it.unitPrice)} [NEW]`));
  diff.removed.forEach((it) => bodyLines.push(`  - ${toText(it.description, 'Item')} (${toText(it.location, '-')}): qty ${formatPoQuantity(it.orderQuantity ?? it.order_quantity)} @ ${formatPoCurrency(it.unitPrice ?? it.unit_price)} [REMOVED]`));
  const text = headerLines.concat(bodyLines, ['', buildPurchaseOrderFooter()]).join('\n');
  return { subject, text, html: `<pre>${text}</pre>` };
}

export async function fetchPurchaseOrders(storeId, filters = {}) {
  if (!storeId) return [];
  const { fromIso, toIso, search, vendorId, status, limit = 200, excludeArchived } = filters || {};
  let query = supabase
    .from('purchase_orders')
    .select('id, po_number, store_id, vendor_id, vendor_name_snapshot, vendor_contact_snapshot, ordered_by_name, ordered_by_email, ordered_by_user_id, source_type, notes, currency, subtotal, total, line_count, sent_from_email, delivery_status, resend_message_id, sent_at, delivered_at, opened_at, clicked_at, bounced_at, complained_at, approval_required, approval_status, parent_po_id, revision_number, is_latest_revision, revision_summary, last_revised_at, created_at, updated_at, requested_delivery_date, delivery_notes, archived_at, canceled_at, voided_at, replaced_at, replacement_for_po_id, replaced_by_po_id, cancellation_reason, void_reason, contact_updated_from, purchase_order_delivery_events(id, event_type, occurred_at)')
    .eq('store_id', storeId)
    .order('created_at', { ascending: false })
    .limit(Math.min(Math.max(Number(limit) || 200, 1), 500));
  if (fromIso) query = query.gte('created_at', fromIso);
  if (toIso) query = query.lte('created_at', toIso);
  if (vendorId) query = query.eq('vendor_id', vendorId);
  if (excludeArchived) query = query.is('archived_at', null);
  // Archived visibility rules:
  //   'archived'        → show only archived POs
  //   '' or undefined   → show everything (no archived_at filter) — used by "All statuses"
  //   any other truthy  → always hide archived POs
  if (status === 'archived') {
    query = query.not('archived_at', 'is', null);
  } else if (status) {
    // Covers active_open, received_closed, canceled_voided, sent, draft, etc.
    query = query.is('archived_at', null);
  }
  // undefined / '' → no archived_at filter → all rows visible
  if (status === 'received_closed') {
    query = query.in('delivery_status', ['received', 'closed']);
  } else if (status === 'canceled_voided') {
    query = query.in('delivery_status', ['canceled', 'replaced', 'voided']);
  } else if (status === 'sent_family') {
    const SENT_FAMILY = ['sent', 'delivered', 'confirmed', 'opened', 'clicked', 'sending',
      'pending_approval', 'blocked_no_email', 'bounced', 'error', 'blocked'];
    query = query.in('delivery_status', SENT_FAMILY);
  } else if (status === 'active_open') {
    const INACTIVE = ['canceled', 'replaced', 'voided', 'received', 'closed', 'revised'];
    query = query.not('delivery_status', 'in', `(${INACTIVE.join(',')})`);
  } else if (status && status !== 'archived') {
    query = query.eq('delivery_status', status);
  }
  if (search) {
    const escaped = String(search).trim().replace(/[%_]/g, (m) => `\\${m}`);
    if (escaped) query = query.or(`po_number.ilike.%${escaped}%,vendor_name_snapshot.ilike.%${escaped}%`);
  }
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchPurchaseOrderById(purchaseOrderId) {
  if (!purchaseOrderId) return null;
  const [poResult, itemsResult, deliveryResult, activityResult, revisionsResult] = await Promise.all([
    supabase
      .from('purchase_orders')
      .select('*')
      .eq('id', purchaseOrderId)
      .maybeSingle(),
    supabase
      .from('purchase_order_items')
      .select('*')
      .eq('purchase_order_id', purchaseOrderId)
      .order('sort_order', { ascending: true })
      .order('id', { ascending: true }),
    supabase
      .from('purchase_order_delivery_events')
      .select('*')
      .eq('purchase_order_id', purchaseOrderId)
      .order('occurred_at', { ascending: true }),
    supabase
      .from('purchase_order_activity_log')
      .select('*')
      .eq('purchase_order_id', purchaseOrderId)
      .order('created_at', { ascending: true }),
    supabase
      .from('purchase_order_revisions')
      .select('*')
      .or(`parent_po_id.eq.${purchaseOrderId},revised_po_id.eq.${purchaseOrderId}`)
      .order('revision_number', { ascending: true }),
  ]);
  if (poResult.error) throw new Error(poResult.error.message);
  if (!poResult.data) return null;
  const po = poResult.data;

  // Fetch linked PO numbers via separate point-lookups to avoid PostgREST
  // self-referential join syntax issues with non-standard FK names.
  const [linkedByResult, linkedForResult] = await Promise.all([
    po.replaced_by_po_id
      ? supabase.from('purchase_orders').select('id, po_number').eq('id', po.replaced_by_po_id).maybeSingle()
      : Promise.resolve({ data: null }),
    po.replacement_for_po_id
      ? supabase.from('purchase_orders').select('id, po_number').eq('id', po.replacement_for_po_id).maybeSingle()
      : Promise.resolve({ data: null }),
  ]);

  return {
    ...po,
    replaced_by_po: linkedByResult.data || null,
    replacement_for_po: linkedForResult.data || null,
    items: itemsResult.data || [],
    deliveryEvents: deliveryResult.data || [],
    activity: activityResult.data || [],
    revisions: revisionsResult.data || [],
  };
}

export async function searchPurchaseOrderLineItems(storeId, query, options = {}) {
  if (!storeId) return [];
  const limit = Math.min(Math.max(Number(options.limit) || 80, 1), 200);
  let request = supabase
    .from('purchase_order_items')
    // pack_size without snapshot: this pulls up to 4× the page limit of rows,
    // and a line's snapshot carries the whole conversion blob. The column is
    // backfilled and written by every creation path, so the fallback would buy
    // nothing here for a lot of bytes.
    .select('id, description, brand, location, unit, pack_size, order_quantity, unit_price, line_total, created_at, inventory_item_id, purchase_order:purchase_orders!inner(id, po_number, store_id, vendor_name_snapshot, ordered_by_name, ordered_by_email, sent_at, created_at, delivery_status)')
    .eq('purchase_order.store_id', storeId)
    .order('created_at', { ascending: false })
    .limit(limit * 4);
  if (query) {
    const cleaned = String(query).trim();
    if (cleaned) {
      const escaped = cleaned.replace(/[%_]/g, (m) => `\\${m}`);
      request = request.or(`description.ilike.%${escaped}%,brand.ilike.%${escaped}%,location.ilike.%${escaped}%`);
    }
  }
  const { data, error } = await request;
  if (error) throw new Error(error.message);

  // Group by item key, take most recent.
  const groups = new Map();
  const EXCLUDED_STATUSES = new Set(['canceled', 'replaced', 'voided', 'draft']);
  (data || []).filter((row) => !EXCLUDED_STATUSES.has(row.purchase_order?.delivery_status)).forEach((row) => {
    const key = row.inventory_item_id != null
      ? `id:${row.inventory_item_id}`
      : `desc:${toText(row.description).toLowerCase()}|loc:${toText(row.location).toLowerCase()}`;
    const occurrence = {
      orderId: row.purchase_order?.id || null,
      poNumber: row.purchase_order?.po_number || '',
      vendorName: row.purchase_order?.vendor_name_snapshot || '',
      orderedByName: row.purchase_order?.ordered_by_name || row.purchase_order?.ordered_by_email || '',
      sentAt: row.purchase_order?.sent_at || row.purchase_order?.created_at || row.created_at,
      quantity: Number(row.order_quantity || 0),
      unitPrice: Number(row.unit_price || 0),
      lineTotal: Number(row.line_total || 0),
      deliveryStatus: row.purchase_order?.delivery_status || '',
    };
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        inventoryItemId: row.inventory_item_id,
        description: row.description,
        brand: row.brand,
        location: row.location,
        unit: row.unit,
        packSize: resolvePoLinePackSize(row),
        latest: occurrence,
        history: [occurrence],
      });
    } else {
      const group = groups.get(key);
      group.history.push(occurrence);
      if (new Date(occurrence.sentAt || 0) > new Date(group.latest.sentAt || 0)) {
        group.latest = occurrence;
      }
    }
  });
  return Array.from(groups.values()).slice(0, limit);
}

export async function sendRevisedPurchaseOrder(originalPurchaseOrderId, revisedPurchaseOrder, options = {}) {
  return invokePurchaseOrderFunction('dispatch_revision', {
    originalPurchaseOrderId,
    revisedPurchaseOrder,
    recipientsOverride: Array.isArray(options.recipientsOverride) ? options.recipientsOverride : undefined,
    requestedDeliveryDate: options.requestedDeliveryDate || null,
    deliveryNotes: options.deliveryNotes || null,
  });
}

export async function emailPurchaseOrderToRecipients(purchaseOrderId, recipients = []) {
  return invokePurchaseOrderFunction('dispatch_custom_recipients', {
    purchaseOrderId,
    recipients,
  });
}

// Backward-compatible exports for the existing vendor send sheet flow.
export function buildVendorOrderPayload(vendorOrder, sourceMetadata = {}, options = {}) {
  return buildPurchaseOrder(vendorOrder, sourceMetadata, options);
}

export function generateVendorOrderEmail(payload) {
  return previewPurchaseOrder(payload);
}

export async function sendVendorOrder(payload, options = {}) {
  return sendPurchaseOrder(payload, options);
}

export async function sendVendorOrdersBatch(vendorOrders, options = {}) {
  return sendPurchaseOrdersBatch(vendorOrders, options);
}

// ---------------------------------------------------------------------------
// Draft PO management helpers
// ---------------------------------------------------------------------------

/**
 * Update a draft purchase order's fields (delivery date, notes, etc.).
 * Only updates fields present in the patch object.
 */
export async function updateDraftPurchaseOrder(poId, patch = {}) {
  if (!poId) throw new Error('poId is required');
  return invokePurchaseOrderFunction('update_draft', { purchaseOrderId: poId, patch });
}

/**
 * Send an existing draft PO using the purchase-order-dispatch edge function.
 * Accepts optional requestedDeliveryDate, deliveryNotes and deliveryInstructions
 * to update before sending.
 */
export async function sendDraftPurchaseOrder(purchaseOrderId, opts = {}) {
  // Only include delivery fields in the payload when they were explicitly supplied.
  // The edge function uses key-presence checks (hasDeliveryDatePatch / hasDeliveryNotesPatch)
  // to decide whether to patch or preserve existing draft values, so omitting a key
  // here means "keep whatever is already saved on the PO".
  const payload = { purchaseOrderId };
  if ('requestedDeliveryDate' in opts) payload.requestedDeliveryDate = opts.requestedDeliveryDate ?? null;
  if ('deliveryNotes' in opts) payload.deliveryNotes = opts.deliveryNotes ?? null;
  // The vendor's standing instructions, frozen onto this order as it goes out.
  if ('deliveryInstructions' in opts) payload.deliveryInstructions = opts.deliveryInstructions ?? null;
  const result = await invokePurchaseOrderFunction('send_draft', payload);

  // Replacement stamping is handled atomically by the DB trigger
  // trg_auto_stamp_replaced_original, which fires within the same transaction
  // that transitions the replacement draft away from 'draft' status.
  return result;
}

/**
 * Create an amendment draft from an existing sent PO.
 * Returns the new draft PO data.
 */
export async function createAmendmentDraft(parentPoId) {
  if (!parentPoId) throw new Error('parentPoId is required');
  const result = await invokePurchaseOrderFunction('create_amendment_draft', { parentPoId });
  if (!result || !result.data) throw new Error('Failed to create amendment draft');
  return result.data;
}

/**
 * Mark a purchase order as received/delivered.
 * Routed through the edge function so it can use service-role permissions
 * (client-side RLS only allows UPDATE on draft POs).
 */
export async function markPurchaseOrderReceived(purchaseOrderId) {
  if (!purchaseOrderId) throw new Error('purchaseOrderId is required');
  const { data, error } = await supabase.rpc('mark_purchase_order_received', { p_po_id: purchaseOrderId });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Create draft purchase orders from a completed count session.
 * Calls the edge function which fetches count_items server-side, groups by vendor,
 * and inserts purchase_orders + purchase_order_items rows with idempotency.
 * Returns { data: { drafts: [{ purchaseOrderId, poNumber, vendorName, vendorId, lineCount, total }], skipped: [...] } }
 */
export async function createDraftPurchaseOrdersFromCount({ countSessionId }) {
  if (!countSessionId) throw new Error('countSessionId is required');
  return invokePurchaseOrderFunction('create_draft_from_count', { countSessionId });
}

/**
 * Update existing draft PO(s) for a count session with the current count values.
 * Replaces each draft's line items and totals with the vendor bucket from the
 * count's current state, and logs a `draft_updated_from_count` activity entry.
 * Returns { data: { updated: [...], skipped: [...] } }
 */
export async function updateDraftFromCount({ countSessionId }) {
  if (!countSessionId) throw new Error('countSessionId is required');
  return invokePurchaseOrderFunction('update_draft_from_count', { countSessionId });
}

/**
 * Create amendment draft(s) from sent PO(s) for a count session, overwriting
 * each new draft's items with the current count state for that vendor.
 * @param {object} opts
 * @param {number} opts.countSessionId
 * @param {number[]} opts.sentPoIds - IDs of the sent POs to amend
 * Returns { data: { amendmentDrafts: [...], skipped: [...] } }
 */
export async function amendSentFromCount({ countSessionId, sentPoIds }) {
  if (!countSessionId) throw new Error('countSessionId is required');
  if (!Array.isArray(sentPoIds) || sentPoIds.length === 0) throw new Error('sentPoIds is required');
  return invokePurchaseOrderFunction('amend_sent_from_count', { countSessionId, sentPoIds });
}

/**
 * Delete a draft purchase order (irreversible).
 */
export async function deleteDraftPurchaseOrder(purchaseOrderId) {
  if (!purchaseOrderId) throw new Error('purchaseOrderId is required');
  return invokePurchaseOrderFunction('delete_draft', { purchaseOrderId });
}

/**
 * Insert a new line item on a draft purchase order from an inventory item.
 * Returns the inserted row.
 */
export async function addDraftItem(poId, invItem, sortOrder) {
  if (!poId) throw new Error('poId is required');
  if (!invItem) throw new Error('invItem is required');

  // A purchase order is a document sent to a vendor, so the line has to be in
  // the vendor's own terms: one CASE at the case price, not one gallon at a
  // per-gallon price the vendor never quoted. When the item was reached
  // through a purchase option, those facts come with it and the line is
  // written in packages. Without one — a legacy item stamped with this vendor
  // — the line keeps the count unit and unit price it always had.
  const packageUnit = String(invItem.purchase_unit || '').trim();
  const packagePrice = parseFloat(invItem.package_price);
  const orderInPackages = !!packageUnit && Number.isFinite(packagePrice) && packagePrice > 0;

  const unit = orderInPackages ? packageUnit : (invItem.unit || '');
  const unitPrice = orderInPackages ? packagePrice : (parseFloat(invItem.unit_price) || 0);

  // The pack belongs to the package, so it rides along only when the line is
  // written in packages — a legacy line ordered in count units has no vendor
  // pack to state, and stating one would describe a different purchase.
  const packSize = orderInPackages
    ? (String(invItem.pack_description || invItem.pack_size || '').trim() || null)
    : null;

  const { data, error } = await supabase
    .from('purchase_order_items')
    .insert({
      purchase_order_id: poId,
      inventory_item_id: invItem.id || null,
      description: invItem.description || '',
      location: invItem.location || '',
      unit,
      pack_size: packSize,
      order_quantity: 1,
      unit_price: unitPrice,
      line_total: unitPrice,
      sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
      // The option is the line's provenance — the favored-item selector reads
      // it as "this member was ordered". It exists only when the line was
      // reached through a package; a legacy line has no option to name.
      purchase_option_id: orderInPackages ? (invItem.purchase_option_id || null) : null,
    })
    .select()
    .maybeSingle();
  if (error) throw new Error(`Add item failed: ${error.message}`);
  return data;
}

/**
 * Persist quantity edits and removals to a draft PO's line items, then update
 * the parent draft's subtotal/total/line_count. Designed to be called once
 * from the DraftOrderReview save/send flow.
 *
 * @param {string} poId
 * @param {object} opts
 * @param {Array<object>} opts.items   The currently loaded line items.
 * @param {object} opts.editedQtys     Map of itemId -> new quantity.
 * @param {Set<string>|Array<string>} opts.removedIds  Item ids marked for removal.
 * @returns {Promise<{ total:number, lineCount:number }>}
 */
// ---------------------------------------------------------------------------
// PO lifecycle: cancel, void, archive, replacement, duplicate
// ---------------------------------------------------------------------------

/**
 * Cancel a sent (non-draft) purchase order.
 * Records the cancellation reason and stamps canceled_at.
 */
export async function cancelPurchaseOrder(poId, { reason } = {}) {
  if (!poId) throw new Error('poId is required');
  const { data, error } = await supabase.rpc('cancel_purchase_order', {
    p_po_id: poId,
    p_reason: reason || null,
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Void a received/closed purchase order.
 * Records the void reason and stamps voided_at.
 */
export async function voidPurchaseOrder(poId, { reason } = {}) {
  if (!poId) throw new Error('poId is required');
  const { data, error } = await supabase.rpc('void_purchase_order', {
    p_po_id: poId,
    p_reason: reason || null,
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Archive a purchase order (sets archived_at; does not change delivery_status).
 */
export async function archivePurchaseOrder(poId) {
  if (!poId) throw new Error('poId is required');
  const { data, error } = await supabase.rpc('archive_purchase_order', { p_po_id: poId });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Unarchive a purchase order (clears archived_at).
 */
export async function unarchivePurchaseOrder(poId) {
  if (!poId) throw new Error('poId is required');
  const { data, error } = await supabase.rpc('unarchive_purchase_order', { p_po_id: poId });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Create a replacement draft PO from an existing sent/received/closed PO.
 * The new draft has replacement_for_po_id pointing back to the original.
 * Returns { success, draft_po_id }.
 */
export async function createReplacementDraft(originalPoId) {
  if (!originalPoId) throw new Error('originalPoId is required');
  const { data, error } = await supabase.rpc('create_replacement_draft_po', {
    p_original_po_id: originalPoId,
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Stamp the original PO as 'replaced' after a replacement draft has been sent.
 * Sets replaced_at and replaced_by_po_id on the original.
 * @param {number} originalPoId - The original PO that was replaced
 * @param {number} replacementPoId - The new PO that replaced it
 */
export async function stampPoReplaced(originalPoId, replacementPoId) {
  if (!originalPoId || !replacementPoId) throw new Error('originalPoId and replacementPoId are required');
  const { data, error } = await supabase.rpc('stamp_po_replaced', {
    p_original_po_id: originalPoId,
    p_replacement_po_id: replacementPoId,
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Duplicate a purchase order as an independent draft (no replacement linkage).
 * Returns { success, draft_po_id }.
 */
export async function duplicatePurchaseOrderAsDraft(poId) {
  if (!poId) throw new Error('poId is required');
  const { data, error } = await supabase.rpc('duplicate_purchase_order_as_draft', { p_po_id: poId });
  if (error) throw new Error(error.message);
  return data;
}

const _VENDOR_ID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function _isValidVendorId(v) {
  if (v == null) return false;
  if (_VENDOR_ID_UUID_RE.test(String(v))) return true;
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}

/**
 * Propagate fresh vendor contact info to all open draft POs for a given vendor + store.
 * Should be called after any vendor contact save (VendorEditor or VendorContactModal).
 * Returns { success, updatedCount }.
 */
export async function propagateVendorContactToDrafts(vendorId, storeId) {
  const numStoreId = Number(storeId);
  if (!_isValidVendorId(vendorId)) throw new Error('A valid vendor must be selected before saving.');
  if (!Number.isFinite(numStoreId) || numStoreId <= 0) throw new Error('Store not identified — cannot save.');
  return invokePurchaseOrderFunction('propagate_vendor_contact', { vendorId, storeId: numStoreId });
}

/**
 * Atomically save vendor contact fields and propagate the fresh snapshot to all
 * open drafts for that vendor + store in a single server-side action.
 * Replaces the separate client-side update + propagation call so both steps
 * either succeed or fail together.
 * Returns { success, updatedVendor, updatedCount }.
 */
export async function saveVendorContactAndPropagate(vendorId, storeId, contactFields) {
  const numStoreId = Number(storeId);
  if (!_isValidVendorId(vendorId)) throw new Error('A valid vendor must be selected before saving.');
  if (!Number.isFinite(numStoreId) || numStoreId <= 0) throw new Error('Store not identified — cannot save.');
  return invokePurchaseOrderFunction('save_vendor_contact', { vendorId, storeId: numStoreId, contactFields });
}

export async function updateDraftItems(poId, { items = [], editedQtys = {}, editedPrices = {}, removedIds = new Set() } = {}) {
  if (!poId) throw new Error('poId is required');
  const removed = removedIds instanceof Set ? removedIds : new Set(removedIds || []);
  const errors = [];
  // A line whose price the count could not resolve carries NULL, not 0
  // (migration 20260821120000), and the buyer supplies it here. An edited
  // price that is not a number leaves the line unpriced rather than writing
  // the 0 that started all this.
  const editedPriceOf = (it) => {
    if (!Object.prototype.hasOwnProperty.call(editedPrices, it.id)) return undefined;
    const parsed = parseFloat(editedPrices[it.id]);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };
  const effectivePriceOf = (it) => {
    const edited = editedPriceOf(it);
    if (edited !== undefined) return edited;
    const stored = parseFloat(it.unit_price);
    return Number.isFinite(stored) ? stored : null;
  };
  const effectiveQtyOf = (it) => {
    if (!Object.prototype.hasOwnProperty.call(editedQtys, it.id)) return parseFloat(it.order_quantity) || 0;
    const qty = parseFloat(editedQtys[it.id]);
    return Number.isFinite(qty) ? qty : 0;
  };

  for (const it of items) {
    if (removed.has(it.id)) {
      const { data: deletedRows, error } = await supabase.from('purchase_order_items').delete().eq('id', it.id).select('id');
      if (error) {
        errors.push(`Delete ${it.description || it.id}: ${error.message}`);
      } else if (!deletedRows || deletedRows.length === 0) {
        errors.push(`Delete ${it.description || it.id}: no rows were removed (item may not belong to this draft or access was denied)`);
      }
      continue;
    }
    const qtyEdited = Object.prototype.hasOwnProperty.call(editedQtys, it.id);
    const priceEdited = editedPriceOf(it) !== undefined;
    if (!qtyEdited && !priceEdited) continue;

    const safeQty = effectiveQtyOf(it);
    const price = effectivePriceOf(it);
    const patch = { order_quantity: safeQty, line_total: price === null ? null : safeQty * price };
    if (priceEdited) patch.unit_price = price;
    const { data: updatedRows, error } = await supabase
      .from('purchase_order_items')
      .update(patch)
      .eq('id', it.id)
      .select('id');
    if (error) {
      errors.push(`Update ${it.description || it.id}: ${error.message}`);
    } else if (!updatedRows || updatedRows.length === 0) {
      errors.push(`Update ${it.description || it.id}: no rows were modified (item may not belong to this draft or access was denied)`);
    }
  }
  if (errors.length > 0) throw new Error(errors.join('; '));

  const remaining = items.filter((it) => !removed.has(it.id));
  // An unpriced line contributes nothing to the total rather than pulling it
  // to a number the buyer would read as complete; the count of such lines is
  // what the send gate refuses on.
  const total = remaining.reduce((sum, it) => {
    const price = effectivePriceOf(it);
    return price === null ? sum : sum + effectiveQtyOf(it) * price;
  }, 0);
  const lineCount = remaining.length;

  try {
    await updateDraftPurchaseOrder(poId, { subtotal: total, total, line_count: lineCount });
  } catch (err) {
    // Items mutated successfully but the parent draft summary did not.
    // Surface a clear error so callers don't show a stale total to the user.
    const detail = err?.message || String(err);
    throw new Error(`Items saved, but draft total could not be updated: ${detail}`);
  }
  return { total, lineCount };
}

